import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  PlanStatusToggleRequest,
  PulseDurationRow,
  TtsPlanCreateRequest,
  TtsPlanRow,
  VoicePlanCreateRequest,
  VoicePlanRow,
} from './types';

export const planKeys = {
  all: ['plans'] as const,
  voiceList: (userId: string | number | null) =>
    ['plans', 'voice', 'list', userId] as const,
  ttsList: (userId: string | number | null) =>
    ['plans', 'tts', 'list', userId] as const,
  pulseDurations: (userId: string | number | null) =>
    ['plans', 'pulse-durations', userId] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: planKeys.all });
}

/* ---------------- Voice Plans ---------------- */

/** GET /api/obd/plans/{userId} */
export function useVoicePlans() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: planKeys.voiceList(userId),
    queryFn: () => api.get<VoicePlanRow[]>(`/api/obd/plans/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<VoicePlanRow>(d),
  });
}

/** GET /api/obd/pulse/{userId} — dropdown source for voice plan create. */
export function usePulseDurations() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: planKeys.pulseDurations(userId),
    queryFn: () =>
      api.get<PulseDurationRow[]>(`/api/obd/pulse/${userId}`),
    enabled: Boolean(userId),
    staleTime: 5 * 60_000,
    select: (d) => ensureArray<PulseDurationRow>(d),
  });
}

/** POST /api/obd/plan */
export function useCreateVoicePlan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: VoicePlanCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/plan', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/plan/update/status — voice plan status toggle. */
export function useToggleVoicePlanStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: PlanStatusToggleRequest) =>
      api.post<{ message?: string }>('/api/obd/plan/update/status', body),
    onSuccess: invalidate,
  });
}

/* ---------------- TTS Plans ---------------- */

/** GET /api/obd/tts/plans/{userId} */
export function useTtsPlans() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: planKeys.ttsList(userId),
    queryFn: () => api.get<TtsPlanRow[]>(`/api/obd/tts/plans/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (d) => ensureArray<TtsPlanRow>(d),
  });
}

/** POST /api/obd/tts/plan */
export function useCreateTtsPlan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TtsPlanCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/tts/plan', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/tts/plan/update/status — TTS plan status toggle. */
export function useToggleTtsPlanStatus() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: PlanStatusToggleRequest) =>
      api.post<{ message?: string }>(
        '/api/obd/tts/plan/update/status',
        body,
      ),
    onSuccess: invalidate,
  });
}

/* ---------------- Shared delete ---------------- */

/** DELETE /api/obd/plan/{planId} — used for BOTH voice and TTS plans. */
export function useDeletePlan() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (planId: number) =>
      api.delete<{ message?: string }>(`/api/obd/plan/${planId}`),
    onSuccess: invalidate,
  });
}
